import { Injectable, computed, inject } from '@angular/core';
import { AuthService } from './auth.service';
import { WorkflowAction } from './ops.service';
import {
  AppModule,
  MODULE_ACCESS_PERMISSION,
  MODULE_ROLES,
  WORKFLOW_ACTION_ROLES,
  anyRoleAllowed,
  canAccessModule,
  isAssigneeScoped,
  isInvestigatorDesk,
} from '../rbac/rbac.policy';
import { OfficerRole } from '../models/officer.model';

@Injectable({ providedIn: 'root' })
export class RbacService {
  private readonly auth = inject(AuthService);

  readonly role = computed(() => {
    const o = this.auth.officer();
    if (!o) return null;
    return (o.activeRole || o.role) ?? null;
  });

  readonly roles = computed(() => {
    const o = this.auth.officer();
    if (!o) return [] as string[];
    return o.roles?.length ? o.roles : o.role ? [o.role] : [];
  });

  readonly permissionCodes = computed(
    () => this.auth.officer()?.permissionCodes ?? [],
  );

  readonly fullAccess = computed(
    () => this.role() === 'admin' && this.auth.officer()?.fullAccess === true,
  );

  /** Built-in officer or any Admin-created investigative desk. */
  readonly isInvestigator = computed(() =>
    this.roles().some((r) => isInvestigatorDesk(r)),
  );

  /** Sees only complaints assigned to their user id (primary role — matches API scope style). */
  readonly isAssigneeScoped = computed(() => isAssigneeScoped(this.role()));

  /**
   * Module visibility from live DB permission matrix (session.permissionCodes).
   * Super Admin always allowed (safety fallback — never lock out admin).
   */
  canModule(module: AppModule): boolean {
    return canAccessModule(
      this.role(),
      this.permissionCodes(),
      module,
      this.fullAccess(),
    );
  }

  /** True when the session grants a specific catalog code (admin / fullAccess always true). */
  hasPermission(code: string): boolean {
    if (!code) return false;
    if (this.fullAccess()) return true;
    return this.permissionCodes().includes(code);
  }

  canWorkflowAction(action: WorkflowAction): boolean {
    return anyRoleAllowed(this.roles(), WORKFLOW_ACTION_ROLES[action], this.role());
  }

  filterWorkflowActions(actions: WorkflowAction[]): WorkflowAction[] {
    return actions.filter((a) => this.canWorkflowAction(a));
  }

  /** Roles that historically could open a module (docs / messaging only). */
  rolesForModule(module: AppModule): OfficerRole[] {
    return MODULE_ROLES[module];
  }

  accessPermissionFor(module: AppModule): string {
    return MODULE_ACCESS_PERMISSION[module];
  }
}
